import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Lock, CheckCircle, ShieldCheck, Zap, HeartHandshake, Wallet, Gift, Gamepad2, MessageSquare, Smartphone, Megaphone, PhoneCall } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import FlatIcon from "@/components/site/FlatIcon";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const useCases = [
  {
    title: "Instant Player Verification",
    description: "Verify identity and age at signup with fast OTP delivery.",
    Icon: Lock,
    color: "text-amber",
    tags: ["SMS"],
  },
  {
    title: "Account Security Alerts",
    description: "Notify players of logins, password resets, or suspicious account activity in real time.",
    Icon: ShieldCheck,
    color: "text-rose",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Deposit & Withdrawal Alerts",
    description: "Confirm transactions instantly to keep players informed and reduce disputes.",
    Icon: Wallet,
    color: "text-lime",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Reward & Bonus Notifications",
    description: "Let players know when they've earned in-game rewards or bonuses.",
    Icon: Gift,
    color: "text-pink",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Player Reactivation",
    description: "Re-engage inactive players with timely, personalized offers, reminders, and bonus updates.",
    Icon: Megaphone,
    color: "text-violet",
    tags: ["SMS"],
  },
];

const enhancedBenefits = [
  {
    title: "Reduce Player Drop-Off",
    description: "Fast OTP and verification delivery keeps sign-ups moving and players engaged.",
    Icon: Zap,
    color: "text-amber",
  },
  {
    title: "Strengthen Account Security",
    description: "Real-time alerts help catch suspicious logins and protect player accounts.",
    Icon: ShieldCheck,
    color: "text-sky",
  },
  {
    title: "Build Player Loyalty",
    description: "Timely, relevant messaging keeps players informed and coming back.",
    Icon: HeartHandshake,
    color: "text-lime",
  },
];

const supportedChannels = [
  { title: "SMS", description: "Deliver OTPs and alerts over the most universally reachable channel.", Icon: MessageSquare, gradient: "icon-tile-teal" },
  { title: "RCS", description: "Use interactive messaging for verification and reward notifications.", Icon: Megaphone, gradient: "icon-tile-indigo" },
  { title: "Voice", description: "Deliver time-sensitive alerts by automated voice call.", Icon: PhoneCall, gradient: "icon-tile-blue" },
];

const faqs = [
  { question: "What is iGaming SMS messaging?", answer: "iGaming SMS messaging refers to text messages sent by online gaming platforms to communicate with players, including signup verification, security alerts, transaction confirmations, bonus notifications, and re-engagement campaigns. It's a fast, direct way to keep players informed and secure." },
  { question: "Is SMS messaging allowed for iGaming?", answer: "Yes, in many regulated markets. Operators must follow local gaming and telecom regulations, including anti-spam and consent rules. Transactional messages like OTPs and security alerts are typically treated differently from promotional marketing messages." },
  { question: "How fast are OTPs delivered to players?", answer: "Because Yootelco connects directly to mobile networks, OTPs are typically delivered within seconds. Speed can vary by destination country and channel, but our infrastructure is built for low-latency, high-volume delivery." },
  { question: "Can iGaming platforms use multiple channels?", answer: "Yes. Operators can reach players over SMS, RCS, and voice, choosing the right channel for verification, security alerts, rewards, and reactivation." },
  { question: "How does messaging help with player retention?", answer: "Timely, personalized messages, such as bonus updates, balance alerts, and re-engagement offers, keep players informed and encourage repeat activity. Direct messaging also builds trust by keeping players updated on account security." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const channelIcons = [
  { label: "SMS", Icon: MessageSquare },
  { label: "RCS", Icon: Megaphone },
];

const IndustriesIGaming = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="industries-page min-h-screen bg-white">
      <Seo
        title="SMS & OTP Messaging for iGaming Platforms – Yootelco"
        description="Instant verification, account security alerts, and high-volume SMS routing built for iGaming platforms that verify players fast."
        path="/industries/igaming"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Verify Players. Deliver Instantly.
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Messaging for iGaming
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Instant verification, account security alerts, and high-volume SMS routing, built for the speed and scale iGaming platforms demand. Yootelco helps gaming operators verify players and deliver time-sensitive updates without delay.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {channelIcons.map((channel) => (
                <div key={channel.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <channel.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {channel.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why iGaming Platforms Choose Yootelco */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Why iGaming Platforms Choose Yootelco</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            With more than 18 years of experience in the gaming industry, our team understands what it takes to launch, scale, and optimize successful gaming campaigns. We know which acquisition strategies perform best, how to navigate advertising platform policies, and how to adapt campaigns to meet regional regulations and audience expectations.
          </p>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            From optimizing user acquisition costs to refining creatives, targeting, and campaign structure, we help maximize performance while ensuring compliance with platform guidelines and local regulations.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Use Cases of iGaming Messaging</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {useCases.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} className="mx-auto block w-fit" />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Enhanced Benefits */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Enhanced Benefits for iGaming</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {enhancedBenefits.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <FlatIcon Icon={Icon} color={item.color} />
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Stay Connected and Compliant */}
      <section className="section-shell">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Stay Connected and Compliant</h2>
          <div className="mt-[24px] mx-auto max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "UK-based team with enterprise messaging expertise",
              "Multi-channel delivery across SMS, RCS, and Voice",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Channels */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 grid-balanced-3">
          {supportedChannels.map((channel) => {
            const Icon = channel.Icon;
            return (
              <article
                key={channel.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-8 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${channel.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{channel.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{channel.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Talk to Sales"
        description="Tell us your use case, we'll show you how Yootelco can support your iGaming messaging needs."
        useCase="iGaming"
      />

      {/* FAQ */}
      <section className="section-shell">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-shell">
        <div className="mx-auto max-w-[800px] rounded-[36px] border border-border/70 bg-gradient-to-br from-secondary/60 to-card px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <h2 className="section-title">Ready to Power Your iGaming Messaging?</h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-muted-foreground">
            Talk to our team about player verification, security alerts, and retention messaging for your platform.
          </p>
          <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default IndustriesIGaming;
